"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { BrandLogo } from "@/src/components/brand-logo";
import {
  advanceTurn,
  afterCnpjLookup,
  chatInputMode,
  maskChatInput,
  qualificationLines,
  startTurn,
  type ChatReply,
  type ChatStep,
  type Qualification,
  type ScriptTurn,
} from "@/src/components/chat/script";
import { payloadManual, type CnpjConsulta } from "@/src/lib/cnpj";

type ChatMessage = {
  id: number;
  role: "bot" | "user";
  text: string;
};

function ChatHeader() {
  return (
    <header className="shrink-0 border-b border-[#001529]/8 bg-[#001529] text-white">
      <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-3 px-4">
        <Link href="/" className="inline-flex items-center" aria-label="OBANKER">
          <BrandLogo inverted className="h-7 max-w-[140px]" priority />
        </Link>
        <p className="truncate text-sm font-medium">Pré-análise de crédito</p>
      </div>
    </header>
  );
}

export function ChatView({ origem }: { origem: string | null }) {
  return <ChatScreen origem={origem} />;
}

function ChatScreen({ origem }: { origem: string | null }) {
  const first = startTurn(origem);
  const [step, setStep] = useState<ChatStep>(first.step);
  const [qualification, setQualification] = useState<Qualification>(first.qualification);
  const [replies, setReplies] = useState<ChatReply[]>(first.replies);
  const [acceptText, setAcceptText] = useState(first.acceptText);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    first.botMessages.map((text, index) => ({ id: index + 1, role: "bot" as const, text })),
  );
  const [draft, setDraft] = useState("");
  const [placeholder, setPlaceholder] = useState(first.placeholder);
  const [busy, setBusy] = useState(false);
  const [nextId, setNextId] = useState(first.botMessages.length + 1);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, replies]);

  function resetChat() {
    const turn = startTurn(origem);
    setStep(turn.step);
    setQualification(turn.qualification);
    setReplies(turn.replies);
    setAcceptText(turn.acceptText);
    setMessages(turn.botMessages.map((text, index) => ({ id: index + 1, role: "bot", text })));
    setDraft("");
    setPlaceholder(turn.placeholder);
    setBusy(false);
    setNextId(turn.botMessages.length + 1);
  }

  function pushMessages(items: ChatMessage[]) {
    setMessages((current) => [...current, ...items]);
  }

  function submitValue(value: string, label = value) {
    const trimmed = value.trim();
    if (!trimmed || busy) return;

    if (step === "done" && trimmed === "restart") {
      resetChat();
      return;
    }

    const id = nextId;
    setNextId(id + 1);
    pushMessages([{ id, role: "user", text: label }]);
    setDraft("");
    setBusy(true);
    setReplies([]);

    const digits = step === "cnpj" ? trimmed.replace(/\D/g, "") : "";
    if (step === "cnpj" && digits.length === 14) {
      void lookupCnpj(id, digits);
      return;
    }

    window.setTimeout(() => {
      applyTurn(advanceTurn(step, qualification, trimmed), id);
    }, 450);
  }

  function applyTurn(turn: ScriptTurn, userId: number) {
    const botStart = userId + 1;
    setNextId(botStart + turn.botMessages.length);
    pushMessages(
      turn.botMessages.map((text, index) => ({
        id: botStart + index,
        role: "bot",
        text,
      })),
    );
    setStep(turn.step);
    setQualification(turn.qualification);
    setReplies(turn.replies);
    setAcceptText(turn.acceptText);
    setPlaceholder(turn.placeholder);
    setBusy(false);
  }

  async function lookupCnpj(userId: number, digits: string) {
    let payload: CnpjConsulta = payloadManual(digits);
    try {
      const response = await fetch(`/api/consulta-cnpj/${digits}`, {
        headers: { Accept: "application/json" },
      });
      if (response.ok) payload = (await response.json()) as CnpjConsulta;
    } catch {
      payload = payloadManual(digits);
    }
    applyTurn(afterCnpjLookup(qualification, digits, payload), userId);
  }

  const summary = step === "done" ? qualificationLines(qualification) : [];

  const showChoices = replies.length > 0 && !busy;

  return (
    <div className="flex h-dvh flex-col bg-[#f7f6f3] text-[#001529]">
      <ChatHeader />

      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col px-4 py-4">
        <div
          ref={listRef}
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-2xl border border-[#001529]/8 bg-white p-4 shadow-sm"
        >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  message.role === "user"
                    ? "rounded-br-md bg-linear-to-b from-[#d4af37] to-[#c9a227] text-[#001529]"
                    : "rounded-bl-md bg-[#001529] text-white"
                }`}
              >
                {message.text}
              </div>
            </div>
          ))}

          {busy ? (
            <div className="flex justify-start">
              <div
                className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-[#001529]/8 px-3.5 py-3"
                aria-label="Digitando"
              >
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#001529]/45 [animation-delay:0ms]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#001529]/45 [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#001529]/45 [animation-delay:300ms]" />
              </div>
            </div>
          ) : null}

          {summary.length > 0 ? (
            <div className="rounded-xl border border-[#001529]/10 bg-[#f7f6f3] p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#b8860b]">Pré-análise anotada</p>
              <dl className="mt-3 space-y-2 text-sm">
                {summary.map((row) => (
                  <div key={row.label} className="flex justify-between gap-4">
                    <dt className="text-[#001529]/55">{row.label}</dt>
                    <dd className="text-right font-medium">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 text-xs leading-relaxed text-[#001529]/55">
                Protótipo: nada foi salvo. No fluxo real, isso cairia em{" "}
                <Link href="/painel/leads" className="font-medium text-[#174785] underline">
                  /painel/leads
                </Link>
                .
              </p>
            </div>
          ) : null}

          {showChoices ? (
            <div className="flex justify-start">
              {step === "tipo" ? (
                <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                  {replies.map((reply) => (
                    <button
                      key={reply.value}
                      type="button"
                      onClick={() => submitValue(reply.value, reply.label)}
                      className="rounded-xl border border-[#001529]/15 bg-white px-3 py-3 text-left transition-colors hover:border-[#c9a227] hover:bg-[#c9a227]/10"
                    >
                      <span className="block text-sm font-semibold">{reply.label}</span>
                      {reply.hint ? (
                        <span className="mt-0.5 block text-xs text-[#001529]/55">{reply.hint}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex w-full max-w-md flex-col gap-2">
                  {replies.map((reply) => (
                    <button
                      key={reply.value}
                      type="button"
                      onClick={() => submitValue(reply.value, reply.label)}
                      className={`rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                        step === "consentimento"
                          ? "bg-[#001529] text-white hover:bg-[#001529]/90"
                          : "border border-[#001529]/15 bg-white text-[#001529] hover:border-[#c9a227] hover:bg-[#c9a227]/10"
                      }`}
                    >
                      {reply.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {acceptText ? (
          <form
            className="mt-3 flex shrink-0 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submitValue(draft);
            }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(maskChatInput(step, event.target.value))}
              disabled={busy}
              inputMode={chatInputMode(step)}
              autoComplete={step === "telefone" ? "tel" : step === "email" ? "email" : "off"}
              placeholder={placeholder ?? (showChoices ? "Ou escreva sua resposta…" : "Escreva sua resposta…")}
              className="h-11 flex-1 rounded-xl border border-[#001529]/15 bg-white px-3 text-sm outline-none focus-visible:border-[#c9a227] disabled:bg-[#001529]/4"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              className="h-11 rounded-xl bg-linear-to-b from-[#d4af37] to-[#c9a227] px-4 text-sm font-semibold text-[#001529] disabled:opacity-40"
            >
              Enviar
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
